import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, MessageCircle, PhoneCall, Video, Loader2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

const RATE = 20; // ₹ per minute (server is source of truth; used only for the UI preview)
const DURATIONS = [15, 30, 45, 60];

function toLocalIsoInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ConsultPage() {
  const nav = useNavigate();
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    d.setSeconds(0, 0);
    return toLocalIsoInput(d);
  }, []);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [slotStart, setSlotStart] = useState(defaultStart);
  const [mode, setMode] = useState("chat");
  const [notes, setNotes] = useState("");
  const [paying, setPaying] = useState(false);

  const amount = minutes * RATE;

  const handlePay = async () => {
    if (name.trim().length < 2) return toast.error("Enter your full name");
    if (phone.trim().length < 6) return toast.error("Enter a valid phone number");
    if (!slotStart) return toast.error("Pick a slot");
    setPaying(true);
    try {
      const iso = new Date(slotStart).toISOString();
      const order = await api.consultOrder({
        name: name.trim(), phone: phone.trim(), email: email.trim() || undefined,
        minutes, slot_start: iso, mode, notes: notes.trim() || undefined,
      });

      if (!(await loadRazorpay())) {
        toast.error("Payment SDK failed to load. Check your internet.");
        setPaying(false); return;
      }

      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Dr. Tejakumar Galla",
        description: `${minutes}-min ${mode === "call" ? "voice/video" : "chat"} consultation`,
        order_id: order.order_id,
        prefill: { name, contact: phone, email: email || undefined },
        theme: { color: "#D4AF37" },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (res) => {
          try {
            await api.consultVerify({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — slot confirmed!");
            nav(`/consult/${order.booking_id}`);
          } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Signature verification failed");
            setPaying(false);
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        console.error("payment.failed", r);
        toast.error(r?.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not create order");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />

      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" data-testid="consult-back-link"
                className="flex items-center gap-2 text-[#A19E95] hover:text-[#D4AF37] transition-colors">
            <ChevronLeft size={18} />
            <div>
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">Back · Jyotish · Homeopathy</div>
              <div className="font-heading text-base sm:text-lg">Insight Engine</div>
            </div>
          </Link>
          <div className="text-right shrink-0">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">
              Book · Prepaid Consultation
            </div>
            <div className="text-[9px] text-[#A8896C] font-mono mt-0.5">₹{RATE}/min · Chat or Voice/Video</div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Book a Slot</div>
          <div className="font-heading text-3xl sm:text-4xl text-[#E6E1D6] mt-1">Consultation with Dr. Tejakumar Galla</div>
          <div className="font-mono text-xs text-[#6E6B64] mt-2 max-w-2xl">
            Pick a duration, a slot start-time, and pay ₹{RATE}/minute upfront via Razorpay.
            After payment your private chat room opens with a Jitsi voice/video link.
          </div>
        </div>

        <div className="border border-[#2A2D35] bg-[#181A1F]/60 p-6 sm:p-8 space-y-6" data-testid="consult-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Full name">
              <input data-testid="consult-name" value={name} onChange={e => setName(e.target.value)}
                     className="input-field" placeholder="Your name" />
            </Field>
            <Field label="Phone (WhatsApp preferred)">
              <input data-testid="consult-phone" value={phone} onChange={e => setPhone(e.target.value)}
                     inputMode="tel" className="input-field" placeholder="+91 …" />
            </Field>
            <Field label="Email (optional)">
              <input data-testid="consult-email" value={email} onChange={e => setEmail(e.target.value)}
                     className="input-field" placeholder="you@example.com" />
            </Field>
            <Field label="Mode">
              <div className="flex gap-2">
                <ModeBtn active={mode === "chat"} onClick={() => setMode("chat")}
                         icon={<MessageCircle size={14} />} label="Text chat"
                         testid="consult-mode-chat" />
                <ModeBtn active={mode === "call"} onClick={() => setMode("call")}
                         icon={<Video size={14} />} label="Voice / Video"
                         testid="consult-mode-call" />
              </div>
            </Field>
            <Field label="Duration">
              <Select value={String(minutes)} onValueChange={(v) => setMinutes(parseInt(v, 10))}>
                <SelectTrigger data-testid="consult-minutes" className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
                  {DURATIONS.map(m => (
                    <SelectItem key={m} value={String(m)}>{m} minutes · ₹{m * RATE}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Slot start (your local time)">
              <input type="datetime-local" data-testid="consult-slot-start"
                     value={slotStart} onChange={e => setSlotStart(e.target.value)}
                     min={defaultStart} className="input-field" />
            </Field>
          </div>

          <Field label="Notes for the doctor (optional)">
            <textarea data-testid="consult-notes" value={notes} onChange={e => setNotes(e.target.value)}
                      rows={3} className="input-field resize-none"
                      placeholder="Chief complaint, birth details, or context…" />
          </Field>

          <div className="border-t border-[#2A2D35] pt-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-1">Payable</div>
              <div className="font-heading text-2xl text-[#D4AF37] flex items-center gap-1">
                <IndianRupee size={20} /> {amount}
              </div>
              <div className="text-[10px] text-[#6E6B64] font-mono">{minutes} min × ₹{RATE}</div>
            </div>
            <Button data-testid="consult-pay-btn" disabled={paying} onClick={handlePay}
                    className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] px-6 py-6 font-heading text-base">
              {paying ? <><Loader2 size={16} className="mr-2 animate-spin" /> Processing…</>
                      : <><PhoneCall size={16} className="mr-2" /> Pay ₹{amount} & Confirm Slot</>}
            </Button>
          </div>
        </div>

        <div className="mt-6 text-[10px] text-[#6E6B64] font-mono">
          Payments secured by Razorpay · Test mode. Voice/Video powered by Jitsi Meet (no download required).
        </div>
      </main>

      <style>{`
        .input-field {
          background: #0F1115; border: 1px solid #2A2D35; color: #E6E1D6;
          padding: 0.65rem 0.75rem; font-family: 'IBM Plex Mono', monospace; font-size: 0.875rem;
          width: 100%; border-radius: 2px; outline: none;
        }
        .input-field:focus { border-color: #D4AF37; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">{label}</span>
      {children}
    </label>
  );
}

function ModeBtn({ active, onClick, icon, label, testid }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick}
            className={`flex-1 border px-3 py-2 text-xs font-mono flex items-center justify-center gap-2 transition-colors
              ${active ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                       : "border-[#2A2D35] text-[#A19E95] hover:border-[#A8896C]"}`}>
      {icon} {label}
    </button>
  );
}
